import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useBlob } from '../hooks/useBlob';
import { useUserBlob, UserBlobErrorBanner } from '../hooks/useUserBlob';

// Phase 12: per-user STM edits now persist to commercial.UserBlob.
// Legacy localStorage key kept for one-shot migration on first hit.
const USER_BLOB_KEY = 'merch-stm-edits-v1';
const LEGACY_LS_KEY = 'merch-stm-edits-v1';

const labelStyle = { color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };

function pct(v) {
  if (v == null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}
function dollar(v) {
  if (v == null) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}
function num(v, dp = 0) {
  if (v == null) return '—';
  return v.toFixed(dp);
}

function statusColor(pctAchieved, higher_is_better = true) {
  if (pctAchieved == null) return 'var(--text-muted)';
  const good = higher_is_better ? pctAchieved >= 1 : pctAchieved >= 1;
  const ok   = higher_is_better ? pctAchieved >= 0.9 : pctAchieved >= 0.9;
  if (good) return 'var(--success)';
  if (ok)   return 'var(--warn)';
  return 'var(--danger)';
}

function KpiCard({ title, target, badge, children }) {
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 700 }}>{title}</div>
          {target && <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>Target: {target}</div>}
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
}

function PctBadge({ value, higher_is_better = true }) {
  const color = statusColor(value, higher_is_better);
  return (
    <div style={{
      background: `color-mix(in srgb, ${color} 14%, transparent)`,
      border: `1px solid color-mix(in srgb, ${color} 33%, transparent)`,
      borderRadius: 20, padding: '4px 14px', color, fontSize: 18, fontWeight: 800,
    }}>
      {pct(value)}
    </div>
  );
}

function MetricRow({ label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{label}</span>
      <span style={{ color: color || 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>
        {value}
        {sub && <span style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 400, marginLeft: 6 }}>{sub}</span>}
      </span>
    </div>
  );
}

function SpeedBar({ pct: p }) {
  const color = p == null ? 'var(--border)' : p >= 1 ? 'var(--success)' : p >= 0.5 ? 'var(--warn)' : 'var(--danger)';
  const width = p == null ? 0 : Math.min(100, p * 100);
  return (
    <div style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ width: `${width}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
    </div>
  );
}

export default function Merchandising() {
  const { data, isLoading: loading } = useBlob('merchandising');
  const userBlob = useUserBlob(USER_BLOB_KEY);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [stmEdits, setStmEdits] = useState({});   // { "ProductKey|weekIdx": pctValue }
  const [stmSaved, setStmSaved] = useState(false);
  const hydratedRef = useRef(false);
  const saveBlobRef = useRef(userBlob.scheduleSave);
  saveBlobRef.current = userBlob.scheduleSave;

  // Initial selectedPeriod once data hydrates
  useEffect(() => {
    if (!data || selectedPeriod) return;
    setSelectedPeriod(data.last_actual_period || data.periods?.[0]?.id);
  }, [data, selectedPeriod]);

  // Hydrate STM edits — server-side per-user blob → legacy localStorage → empty.
  useEffect(() => {
    if (userBlob.data === undefined) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    if (userBlob.data && typeof userBlob.data === 'object') {
      setStmEdits(userBlob.data);
      return;
    }
    try {
      const saved = localStorage.getItem(LEGACY_LS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          setStmEdits(parsed);
          userBlob.save(parsed);
          try { localStorage.removeItem(LEGACY_LS_KEY); } catch {}
        }
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBlob.data]);

  const saveStmEdit = useCallback((product, retailer, weekIdx, rawValue) => {
    const key = `${product}|${retailer}|${weekIdx}`;
    const parsed = rawValue === '' ? null : parseFloat(rawValue) / 100;
    setStmEdits(prev => {
      const next = { ...prev, [key]: parsed };
      // Persist to UserBlob (debounced 1s by the hook). Use a ref so we
      // don't recreate this callback on every userBlob update.
      try { saveBlobRef.current(next); } catch {}
      return next;
    });
    setStmSaved(true);
    setTimeout(() => setStmSaved(false), 1500);
  }, []);

  const kpis = useMemo(() => {
    if (!data || !selectedPeriod) return null;
    return data.by_period[selectedPeriod];
  }, [data, selectedPeriod]);

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading…</div>;
  if (!data) return <div style={{ color: 'var(--danger)', padding: 40, textAlign: 'center' }}>Could not load merchandising data. Run build_merchandising.py first.</div>;

  const periodLabel = data.periods.find(p => p.id === selectedPeriod)?.label || selectedPeriod;
  const isActual = kpis?.has_actual;

  return (
    <div>
      {/* Load-failure banner — silent-fail bug fix (Phase 15). */}
      <UserBlobErrorBanner blob={userBlob} noun="merchandising edits" hydrated={hydratedRef.current} />
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>Field Merchandising KPIs</h2>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
            Liaise Marketing · FY26 · Source: Liaise Marketing KPIs FY26.xlsx
          </div>
        </div>
        {/* Period selector */}
        <div>
          <div style={labelStyle}>Period End</div>
          <select
            value={selectedPeriod || ''}
            onChange={e => setSelectedPeriod(e.target.value)}
            style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}
          >
            {data.periods.map(p => (
              <option key={p.id} value={p.id}>
                {p.label}{p.id === data.last_actual_period ? ' ●' : ''}
              </option>
            ))}
          </select>
          {!isActual && (
            <div style={{ color: 'var(--warn)', fontSize: 11, marginTop: 4 }}>Estimate (no actuals yet)</div>
          )}
        </div>
      </div>

      {kpis && (
        <>
          {/* KPI grid — 3 columns top row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 14 }}>

            {/* KPI 1 — Net Sales */}
            <KpiCard
              title="1. Net Sales Performance"
              target="Achieve Budget + Strong Growth"
              badge={<PctBadge value={kpis.net_sales.pct_achieved} />}
            >
              <MetricRow label="YTD Actuals / Estimate" value={dollar(kpis.net_sales.actual_ytd)} />
              <MetricRow label="YTD Budget" value={dollar(kpis.net_sales.budget_ytd)} />
              <MetricRow label="vs Budget" value={pct(kpis.net_sales.pct_vs_budget)} color={kpis.net_sales.pct_vs_budget >= 0 ? 'var(--success)' : 'var(--danger)'} />
              <MetricRow label="vs PY" value={pct(kpis.net_sales.pct_vs_py)} color={kpis.net_sales.pct_vs_py >= 0 ? 'var(--success)' : 'var(--danger)'} />
            </KpiCard>

            {/* KPI 2 — Indie GSV */}
            <KpiCard
              title="2. Independent Customers GSV"
              target="Achieve Budget $"
              badge={<PctBadge value={kpis.indie_gsv.pct_achieved} />}
            >
              <MetricRow label="GSV Actuals / Estimate" value={dollar(kpis.indie_gsv.actual_ytd)} />
              <MetricRow label="Budget YTD" value={dollar(kpis.indie_gsv.budget_ytd)} />
              <MetricRow label="vs Budget" value={pct(kpis.indie_gsv.pct_achieved != null ? kpis.indie_gsv.pct_achieved - 1 : null)} color={kpis.indie_gsv.pct_achieved >= 1 ? 'var(--success)' : 'var(--danger)'} />
              <MetricRow label="vs PY" value={pct(kpis.indie_gsv.pct_vs_py)} color={kpis.indie_gsv.pct_vs_py >= 0 ? 'var(--success)' : 'var(--danger)'} />
            </KpiCard>

            {/* KPI 3 — PODs */}
            <KpiCard
              title="3. Independent Distribution Gains"
              target="8,500 end of FY (start 8,020)"
              badge={<PctBadge value={kpis.pods.pct_achieved} />}
            >
              <MetricRow label="PODs YTD" value={kpis.pods.actual != null ? kpis.pods.actual.toLocaleString() : '—'} />
              <MetricRow label="Target PODs" value={kpis.pods.target != null ? kpis.pods.target.toLocaleString() : '—'} />
              <MetricRow label="Gap to Target" value={kpis.pods.gap != null ? (kpis.pods.gap >= 0 ? `+${kpis.pods.gap}` : kpis.pods.gap) : '—'}
                color={kpis.pods.gap != null ? (kpis.pods.gap >= 0 ? 'var(--success)' : 'var(--danger)') : 'var(--text-muted)'} />
              <MetricRow label="PY PODs" value={kpis.pods.py != null ? kpis.pods.py.toLocaleString() : '—'} />
            </KpiCard>
          </div>

          {/* Second row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 14 }}>

            {/* KPI 4 — Displays */}
            <KpiCard
              title="4. Discretional Off Location Displays"
              target="1,400 per year (117/month)"
              badge={<PctBadge value={kpis.displays.pct_achieved} />}
            >
              <MetricRow label="Displays YTD" value={kpis.displays.actual_ytd != null ? kpis.displays.actual_ytd : '—'} />
              <MetricRow label="Target YTD" value={kpis.displays.target_ytd != null ? Math.round(kpis.displays.target_ytd) : '—'} />
              <MetricRow label="Gap to Target" value={kpis.displays.gap != null ? (kpis.displays.gap >= 0 ? `+${Math.round(kpis.displays.gap)}` : Math.round(kpis.displays.gap)) : '—'}
                color={kpis.displays.gap != null ? (kpis.displays.gap >= 0 ? 'var(--success)' : 'var(--danger)') : 'var(--text-muted)'} />
              <MetricRow label="This Period" value={kpis.displays.monthly != null ? kpis.displays.monthly : '—'} />
            </KpiCard>

            {/* KPI 5 — Non Scans */}
            <KpiCard
              title="5. Chains Non-Scans"
              target="Below 4 weekly average"
              badge={
                <div style={{
                  background: kpis.non_scans.gap != null && kpis.non_scans.gap <= 0 ? 'var(--success-soft)' : 'var(--danger-soft)',
                  border: `1px solid ${kpis.non_scans.gap != null && kpis.non_scans.gap <= 0 ? 'var(--success-border)' : 'var(--danger-border)'}`,
                  borderRadius: 20, padding: '4px 14px',
                  color: kpis.non_scans.gap != null && kpis.non_scans.gap <= 0 ? 'var(--success)' : 'var(--danger)',
                  fontSize: 18, fontWeight: 800,
                }}>
                  {num(kpis.non_scans.avg_ytd, 2)}
                </div>
              }
            >
              <MetricRow label="YTD Running Average" value={num(kpis.non_scans.avg_ytd, 2)} />
              <MetricRow label="Target (max)" value={kpis.non_scans.target} />
              <MetricRow label="Gap" value={kpis.non_scans.gap != null ? (kpis.non_scans.gap > 0 ? `+${num(kpis.non_scans.gap, 2)} above target` : `${num(kpis.non_scans.gap, 2)} below target`) : '—'}
                color={kpis.non_scans.gap != null ? (kpis.non_scans.gap <= 0 ? 'var(--success)' : 'var(--danger)') : 'var(--text-muted)'} />
              <MetricRow label="This Period Total" value={kpis.non_scans.monthly_total != null ? kpis.non_scans.monthly_total : '—'} />
            </KpiCard>
          </div>

          {/* KPI 6 — Speed to Market */}
          <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 700 }}>6. Chains Speed To Market</div>
              {stmSaved && <span style={{ fontSize: 11, color: 'var(--success)' }}>Saved ✓</span>}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 16 }}>
              Target: Full distribution within 4 weeks of ranging · Click <span style={{ color: 'var(--warn)' }}>—</span> to enter missing values
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
              {data.speed_to_market.map(prod => (
                <div key={prod.product} style={{ background: 'var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>{prod.product}</div>
                    <span style={{ background: 'var(--border)', color: 'var(--text-muted)', borderRadius: 10, padding: '1px 8px', fontSize: 10 }}>{prod.retailer}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {prod.weeks.filter(w => w.label).map((w, i) => {
                      const editKey = `${prod.product}|${prod.retailer}|${i}`;
                      const editedPct = stmEdits[editKey];
                      const effectivePct = editedPct !== undefined ? editedPct : w.pct;
                      const hasData = effectivePct != null;
                      const met = hasData && effectivePct >= 1;
                      const isEdited = editedPct !== undefined;
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: !hasData ? 'var(--border)' : met ? 'var(--success)' : 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                            {i + 1}
                          </div>
                          <SpeedBar pct={effectivePct} />
                          {hasData ? (
                            <div style={{ width: 44, textAlign: 'right', color: met ? 'var(--success)' : 'var(--warn)', fontSize: 12, fontWeight: 600, flexShrink: 0, opacity: isEdited ? 0.8 : 1 }}>
                              {pct(effectivePct)}{isEdited && <span style={{ fontSize: 8, color: 'var(--text-muted)' }}> *</span>}
                            </div>
                          ) : (
                            <input
                              type="number" min="0" max="100" step="0.1"
                              placeholder="—"
                              onBlur={e => { if (e.target.value !== '') saveStmEdit(prod.product, prod.retailer, i, e.target.value); }}
                              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                              style={{ width: 44, textAlign: 'right', background: 'var(--bg-input)', border: '1px solid var(--warn-border)', borderRadius: 3, color: 'var(--warn)', fontSize: 11, padding: '1px 4px', flexShrink: 0, outline: 'none' }}
                            />
                          )}
                          <div style={{ width: 130, color: 'var(--text-muted)', fontSize: 10, flexShrink: 0 }}>{w.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
